import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getMasterStats, masterDelete, resetAllStats, getDeleteHistory } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Trash2, 
  AlertTriangle, 
  Users, 
  Phone, 
  Database,
  Loader2,
  ShieldAlert,
  Lock,
  FileText,
  RefreshCw,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  History,
  ChevronLeft,
  ChevronRight,
  Zap,
  MessageSquare,
  ClipboardList
} from 'lucide-react';

export default function MasterDelete() {
  const navigate = useNavigate();
  const { isFeatureRestricted } = useAuth();
  
  // Check access restriction
  useEffect(() => {
    if (isFeatureRestricted('master_delete')) {
      toast.error('Access denied. You do not have permission to access this page.');
      navigate('/dashboard');
    }
  }, [isFeatureRestricted, navigate]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [password, setPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [deleteUsers, setDeleteUsers] = useState(false);
  const [deleteCalls, setDeleteCalls] = useState(false);
  const [deleteReports, setDeleteReports] = useState(false);
  const [deleteAll, setDeleteAll] = useState(false);
  const [deleteAllExceptUsers, setDeleteAllExceptUsers] = useState(false);
  const [deleteQaNotes, setDeleteQaNotes] = useState(false);
  const [deleteReviewRequests, setDeleteReviewRequests] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Delete history state
  const [activeTab, setActiveTab] = useState('delete');
  const [deleteHistory, setDeleteHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchDeleteHistory();
    }
  }, [activeTab, historyPage]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const { data } = await getMasterStats();
      setStats(data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeleteHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await getDeleteHistory(historyPage, 10);
      setDeleteHistory(data.history || []);
      setHistoryTotalPages(data.total_pages || 1);
      setHistoryTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch delete history:', error);
      toast.error('Failed to fetch delete history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteAllChange = (checked) => {
    setDeleteAll(checked);
    if (checked) {
      setDeleteUsers(true);
      setDeleteCalls(true);
      setDeleteReports(true);
      setDeleteQaNotes(true);
      setDeleteReviewRequests(true);
      setDeleteAllExceptUsers(false);
    }
  };

  const handleDeleteAllExceptUsersChange = (checked) => {
    setDeleteAllExceptUsers(checked);
    if (checked) {
      setDeleteUsers(false);
      setDeleteCalls(true);
      setDeleteReports(true);
      setDeleteQaNotes(true);
      setDeleteReviewRequests(true);
      setDeleteAll(false);
    }
  };

  const handleProceed = () => {
    if (!password) {
      toast.error('Please enter the master password');
      return;
    }
    if (!deleteUsers && !deleteCalls && !deleteReports && !deleteAll && !deleteAllExceptUsers && !deleteQaNotes && !deleteReviewRequests) {
      toast.error('Please select at least one option to delete');
      return;
    }
    setShowConfirm(true);
    setConfirmText('');
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const { data } = await masterDelete({
        password,
        delete_users: deleteUsers,
        delete_calls: deleteCalls,
        delete_reports: deleteReports,
        delete_all: deleteAll,
        delete_all_except_users: deleteAllExceptUsers,
        delete_qa_notes: deleteQaNotes,
        delete_review_requests: deleteReviewRequests
      });
      
      toast.success(data.message);
      
      // Reset form
      setPassword('');
      setDeleteUsers(false);
      setDeleteCalls(false);
      setDeleteReports(false);
      setDeleteAll(false);
      setDeleteAllExceptUsers(false);
      setDeleteQaNotes(false);
      setDeleteReviewRequests(false);
      setShowConfirm(false);
      setConfirmText('');
      
      // Refresh stats
      await fetchStats();
      
      // Refresh history if on history tab
      if (activeTab === 'history') {
        await fetchDeleteHistory();
      }
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid master password');
      } else {
        toast.error(error.response?.data?.detail || 'Delete operation failed');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetStats = async () => {
    if (!resetPassword) {
      toast.error('Please enter the master password');
      return;
    }
    
    setIsResetting(true);
    try {
      const { data } = await resetAllStats(resetPassword);
      toast.success(data.message);
      
      if (data.reset_actions?.length > 0) {
        toast.info(`Reset: ${data.reset_actions.join(', ')}`);
      }
      
      setResetPassword('');
      setShowResetConfirm(false);
      await fetchStats();
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Invalid master password');
      } else {
        toast.error(error.response?.data?.detail || 'Reset operation failed');
      }
    } finally {
      setIsResetting(false);
    }
  };

  const getDeletePreview = () => {
    if (!stats) return { count: 0, items: [] };
    
    let count = 0;
    let items = [];
    
    if (deleteAll || deleteUsers) {
      count += stats.users + stats.tenant_users + stats.user_sessions;
      items.push(`${stats.users + stats.tenant_users} users`);
      items.push(`${stats.user_sessions} user sessions`);
    }
    
    if (deleteAll || deleteAllExceptUsers || deleteCalls) {
      count += stats.calls + stats.transcriptions + stats.call_analyses + stats.onedrive_synced_files;
      items.push(`${stats.calls} calls`);
      items.push(`${stats.transcriptions} transcriptions`);
      items.push(`${stats.call_analyses} call analyses`);
      items.push(`${stats.onedrive_synced_files} OneDrive sync records`);
    }
    
    if (deleteAll || deleteAllExceptUsers || deleteReports) {
      count += stats.call_log_notes || 0;
      items.push(`${stats.call_log_notes || 0} call log notes (reports)`);
    }
    
    if (deleteAll || deleteAllExceptUsers || deleteQaNotes) {
      count += stats.qa_notes || 0;
      items.push(`${stats.qa_notes || 0} QA notes`);
    }
    
    if (deleteAll || deleteAllExceptUsers || deleteReviewRequests) {
      count += stats.review_requests || 0;
      items.push(`${stats.review_requests || 0} review requests`);
    }

    // Additional items for delete all except users
    if (deleteAllExceptUsers) {
      items.push('All QA alerts and activities');
      items.push('All compliance alerts');
      items.push('All KPI and scoring configs');
    }
    
    return { count, items };
  };

  const preview = getDeletePreview();

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="master-delete">
      {/* Warning Header */}
      <Card className="border-red-300 bg-red-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-full">
              <ShieldAlert className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-red-800 text-xl">Master Delete - Danger Zone</CardTitle>
              <CardDescription className="text-red-600">
                This action is irreversible. All selected data will be permanently deleted.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs for Delete and History */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="delete" className="gap-2" data-testid="tab-delete">
            <Trash2 className="h-4 w-4" />
            Delete Data
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
            <History className="h-4 w-4" />
            Delete History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="delete" className="space-y-6 mt-6">

      {/* Current Statistics */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Current Data Statistics</CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStats} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : stats ? (
            <>
              {/* Main Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{stats.users + stats.tenant_users}</p>
                  <p className="text-sm text-blue-600">Users</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <Phone className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{stats.calls}</p>
                  <p className="text-sm text-emerald-600">Calls</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <FileText className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-700">{stats.transcriptions}</p>
                  <p className="text-sm text-purple-600">Transcriptions</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <FileText className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-700">{stats.call_log_notes || 0}</p>
                  <p className="text-sm text-amber-600">Reports</p>
                </div>
              </div>

              {/* QA Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 bg-cyan-50 rounded-lg text-center">
                  <MessageSquare className="h-6 w-6 text-cyan-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-cyan-700">{stats.qa_notes || 0}</p>
                  <p className="text-sm text-cyan-600">QA Notes</p>
                </div>
                <div className="p-4 bg-violet-50 rounded-lg text-center">
                  <ClipboardList className="h-6 w-6 text-violet-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-violet-700">{stats.review_requests || 0}</p>
                  <p className="text-sm text-violet-600">Review Requests</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <Database className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-700">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Records</p>
                </div>
              </div>

              {/* Call Status Breakdown */}
              {stats.calls_by_status && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Call Processing Status</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-lg font-semibold text-green-700">{stats.calls_by_status.completed || 0}</p>
                        <p className="text-xs text-green-600">Completed</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="text-lg font-semibold text-blue-700">{stats.calls_by_status.processing || 0}</p>
                        <p className="text-xs text-blue-600">Processing</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-amber-50 rounded">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <div>
                        <p className="text-lg font-semibold text-amber-700">{stats.calls_by_status.pending || 0}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-red-50 rounded">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <div>
                        <p className="text-lg font-semibold text-red-700">{stats.calls_by_status.failed || 0}</p>
                        <p className="text-xs text-red-600">Failed</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Stats */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Additional Counters</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div className="flex justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">OneDrive Synced</span>
                    <span className="font-medium">{stats.onedrive_synced_files || 0}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">Processing Queue</span>
                    <span className="font-medium">{stats.processing_queue || 0}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">Critical Alerts</span>
                    <span className="font-medium text-red-600">{stats.critical_alerts || 0}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">User Sessions</span>
                    <span className="font-medium">{stats.user_sessions || 0}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">Call Analyses</span>
                    <span className="font-medium">{stats.call_analyses || 0}</span>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Reset Stats Card */}
      <Card className="border-amber-300 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
            <RotateCcw className="h-5 w-5" />
            Reset All Counters & Stats
          </CardTitle>
          <CardDescription className="text-amber-700">
            Reset sync stats, clear processing queue, and clean up stuck statuses without deleting actual data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-amber-700">
            This will:
          </p>
          <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
            <li>Reset OneDrive sync statistics</li>
            <li>Clear the call processing queue</li>
            <li>Fix stuck "processing" or "pending" call statuses</li>
            <li>Clear OneDrive sync tracking (allows re-sync of files)</li>
          </ul>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label htmlFor="reset-password" className="text-amber-700">Master Password</Label>
              <Input
                id="reset-password"
                type="password"
                placeholder="Enter master password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button 
              variant="outline" 
              className="border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={() => setShowResetConfirm(true)}
              disabled={!resetPassword}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Stats
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            Select Data to Delete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox 
                id="delete-users" 
                checked={deleteUsers}
                onCheckedChange={setDeleteUsers}
                disabled={deleteAll}
              />
              <div className="flex-1">
                <Label htmlFor="delete-users" className="font-medium cursor-pointer">
                  Delete All Users
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Removes all users, tenant users, and user sessions from the system
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2">
                    {stats.users + stats.tenant_users} users + {stats.user_sessions} sessions
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox 
                id="delete-calls" 
                checked={deleteCalls}
                onCheckedChange={setDeleteCalls}
                disabled={deleteAll}
              />
              <div className="flex-1">
                <Label htmlFor="delete-calls" className="font-medium cursor-pointer">
                  Delete All Calls
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Removes all calls, transcriptions, analyses, and OneDrive sync records
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2">
                    {stats.calls} calls + {stats.transcriptions} transcriptions + {stats.call_analyses} analyses
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <Checkbox 
                id="delete-reports" 
                checked={deleteReports}
                onCheckedChange={setDeleteReports}
                disabled={deleteAll}
                data-testid="delete-reports-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="delete-reports" className="font-medium cursor-pointer">
                  Delete All Reports
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Removes all call log notes and compliance data from the Reports page
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2">
                    {stats.call_log_notes || 0} call log notes
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors border-cyan-200 bg-cyan-50/30">
              <Checkbox 
                id="delete-qa-notes" 
                checked={deleteQaNotes}
                onCheckedChange={setDeleteQaNotes}
                disabled={deleteAll}
                data-testid="delete-qa-notes-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="delete-qa-notes" className="font-medium cursor-pointer">
                  Delete All QA Notes
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Removes all QA notes created by auditors for calls
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2 bg-cyan-100 text-cyan-700">
                    {stats.qa_notes || 0} QA notes
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors border-violet-200 bg-violet-50/30">
              <Checkbox 
                id="delete-review-requests" 
                checked={deleteReviewRequests}
                onCheckedChange={setDeleteReviewRequests}
                disabled={deleteAll}
                data-testid="delete-review-requests-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="delete-review-requests" className="font-medium cursor-pointer">
                  Delete ALL Review Requests
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Removes all review requests from recruiter to QA auditors
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2 bg-violet-100 text-violet-700">
                    {stats.review_requests || 0} review requests
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border-2 border-red-300 rounded-lg bg-red-50 hover:bg-red-100 transition-colors">
              <Checkbox 
                id="delete-all" 
                checked={deleteAll}
                onCheckedChange={handleDeleteAllChange}
                className="border-red-400"
              />
              <div className="flex-1">
                <Label htmlFor="delete-all" className="font-medium cursor-pointer text-red-700">
                  Delete Everything (Full System Wipe)
                </Label>
                <p className="text-sm text-red-600 mt-1">
                  Removes ALL data from the system - users, calls, transcriptions, and everything else
                </p>
                {stats && (
                  <Badge variant="destructive" className="mt-2">
                    {stats.total} total records
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 border-2 border-amber-300 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
              <Checkbox 
                id="delete-all-except-users" 
                checked={deleteAllExceptUsers}
                onCheckedChange={handleDeleteAllExceptUsersChange}
                className="border-amber-400"
                data-testid="delete-all-except-users-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="delete-all-except-users" className="font-medium cursor-pointer text-amber-700">
                  <Zap className="h-4 w-4 inline mr-1" />
                  Delete ALL Data EXCEPT Users
                </Label>
                <p className="text-sm text-amber-600 mt-1">
                  Removes all calls, reports, KPIs, alerts, and submissions while keeping user accounts intact
                </p>
                {stats && (
                  <Badge variant="secondary" className="mt-2 bg-amber-100 text-amber-700">
                    {stats.calls + (stats.call_log_notes || 0) + (stats.transcriptions || 0)} data records
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Password Input */}
          <div className="pt-4 border-t">
            <Label htmlFor="master-password" className="flex items-center gap-2 mb-2">
              <Lock className="h-4 w-4" />
              Master Password
            </Label>
            <Input
              id="master-password"
              type="password"
              placeholder="Enter master password to proceed"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {/* Preview */}
          {(deleteUsers || deleteCalls || deleteReports || deleteAll || deleteAllExceptUsers || deleteQaNotes || deleteReviewRequests) && preview.count > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                <AlertTriangle className="h-5 w-5" />
                Delete Preview
              </div>
              <p className="text-amber-600 text-sm">
                This will permanently delete <strong>{preview.count}</strong> records:
              </p>
              <ul className="text-sm text-amber-600 mt-2 list-disc list-inside">
                {preview.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Button */}
          <Button 
            variant="destructive" 
            className="w-full"
            onClick={handleProceed}
            disabled={(!deleteUsers && !deleteCalls && !deleteReports && !deleteAll && !deleteAllExceptUsers && !deleteQaNotes && !deleteReviewRequests) || !password}
            data-testid="proceed-delete-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Proceed to Delete
          </Button>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Delete History Tab */}
        <TabsContent value="history" className="space-y-4 mt-6" data-testid="history-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Delete History Log
                  </CardTitle>
                  <CardDescription>
                    Track all delete operations performed on the system
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchDeleteHistory} disabled={historyLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : deleteHistory.length > 0 ? (
                <>
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date & Time</TableHead>
                          <TableHead>Action By</TableHead>
                          <TableHead>Delete Type</TableHead>
                          <TableHead>Records Deleted</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deleteHistory.map((entry) => (
                          <TableRow key={entry.id} data-testid={`history-row-${entry.id}`}>
                            <TableCell>
                              <div className="text-sm">
                                {new Date(entry.timestamp).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(entry.timestamp).toLocaleTimeString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{entry.action_by}</span>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={entry.delete_type === 'all' ? 'destructive' : entry.delete_type === 'all_except_users' ? 'secondary' : 'outline'}
                                className="capitalize"
                              >
                                {entry.delete_type?.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold">{entry.total_deleted || 0}</span>
                            </TableCell>
                            <TableCell>
                              {entry.status === 'completed' ? (
                                <Badge variant="default" className="bg-emerald-500">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Completed
                                </Badge>
                              ) : entry.status === 'failed' ? (
                                <Badge variant="destructive">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Failed
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {entry.status}
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>

                  {/* Pagination */}
                  {historyTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing page {historyPage} of {historyTotalPages} ({historyTotal} total entries)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                          disabled={historyPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">Page {historyPage}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                          disabled={historyPage === historyTotalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="font-medium">No Delete History</p>
                  <p className="text-sm text-muted-foreground">
                    Delete operations will be logged here for audit purposes
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 border-red-300">
            <CardHeader className="bg-red-50 border-b border-red-200">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <div>
                  <CardTitle className="text-red-800">Final Confirmation</CardTitle>
                  <CardDescription className="text-red-600">
                    This action cannot be undone!
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <p className="text-slate-700">
                You are about to permanently delete <strong className="text-red-600">{preview.count}</strong> records from the system.
              </p>
              
              <div>
                <Label htmlFor="confirm-text" className="text-sm text-muted-foreground">
                  Type <strong>DELETE</strong> to confirm:
                </Label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  className="mt-2"
                  autoFocus
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowConfirm(false);
                    setConfirmText('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={handleDelete}
                  disabled={confirmText !== 'DELETE' || isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {isDeleting ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 border-amber-300">
            <CardHeader className="bg-amber-50 border-b border-amber-200">
              <div className="flex items-center gap-3">
                <RotateCcw className="h-8 w-8 text-amber-600" />
                <div>
                  <CardTitle className="text-amber-800">Confirm Reset</CardTitle>
                  <CardDescription className="text-amber-600">
                    This will reset all counters and stats
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <p className="text-slate-700">
                This will reset sync statistics, clear the processing queue, and fix stuck call statuses.
                <strong className="block mt-2 text-amber-700">No actual data (calls, users, etc.) will be deleted.</strong>
              </p>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowResetConfirm(false);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleResetStats}
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  {isResetting ? 'Resetting...' : 'Reset All Stats'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
